import { useCallback, useRef } from 'react';

/**
 * Returns a debounced version of a callback. The callback
 * will only be invoked once during the specified `delay`.
 * If it gets called again within that delay, the previous
 * call is canceled.
 *
 * @param fn The function to debounce
 * @param delay The debounce delay in ms
 */
export function useDebounceCallback<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  // Keep track of the current timeout handle
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      // If there’s an existing call scheduled, cancel it
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule a new call
      timeoutRef.current = setTimeout(() => {
        fn(...args);
      }, delay);
    },
    [fn, delay]
  );

  return debouncedFn;
}
